'use client';

import type { ReactNode } from 'react';

import { cn } from '../../utils/cn';

export type ComposerRowVariant = 'header' | 'body' | 'footer' | 'main' | 'compact-main';

interface ComposerRowProps {
	children: ReactNode;
	variant?: ComposerRowVariant;
	className?: string;
}

// Padding spec from Figma:
// - main: p-[16px] — single row compact layout (input + actions)
// - compact-main: py-[12px] px-[16px] — 4px-shorter variant for the mobile
//   collapsed AskNaumuBar; identical flex layout so the row can flip between
//   `main` and `compact-main` without remounting children. The shared
//   `transition-[padding]` keeps the morph smooth when the variant switches.
// - header: pt-[8px] px-[8px] — participants row stacked above
// - body: p-[16px] — expanded multi-line input content; the editor inside owns
//   the 30vh cap + scroll (a second cap here doubles the scrollbar)
// - footer: pb-[8px] px-[8px] — formatting/attachments/send row under body
const variantClasses: Record<ComposerRowVariant, string> = {
	main: 'p-[16px] flex items-center gap-[8px] transition-[padding] duration-200 ease-out',
	'compact-main': 'py-[12px] px-[16px] flex items-center gap-[8px] transition-[padding] duration-200 ease-out',
	header: 'pt-[8px] px-[8px] flex items-center justify-between',
	body: 'p-[16px] flex items-start',
	footer: 'pb-[8px] px-[8px] flex items-center',
};

export const ComposerRow = ({
	children,
	variant = 'main',
	className,
}: ComposerRowProps) => {
	return (
		<div
			className={cn('w-full shrink-0', variantClasses[variant], className)}
			data-row-variant={variant}
		>
			{children}
		</div>
	);
};
